import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '@core/components/base-component';
import { ClassificationHelper } from '@core/helpers/classification.helper';
import { EnumItem } from '@core/models/enum-item.model';
import { ProjectPickerItem } from '@data-managment/project-picker/models/project-picker.item';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { EstimateTypeEnum } from '@enums/EstimateType';
import { PopEntityStateEnum } from '@enums/PopEntityState';
import { EstimateBudgetTypes } from '@estimate/estimate-budget-types/models/estimate-budget-types.model';
import { Classification } from '@interfaces/Classification';
import { Company } from '@interfaces/Company';
import { CompanyCurrency } from '@interfaces/CompanyCurrency';
import { CustomerDto } from '@interfaces/CustomerDto';
import { Department } from '@interfaces/Department';
import { Estimate } from '@interfaces/Estimate';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { Project } from '@interfaces/Project';
import { Resource } from '@interfaces/Resource';
import { EstimateTypeEnumItems } from '@models/consts/estimate-type-enum-items.const';
import { CurrencyService } from '@shared/services/currency.service';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { UserService } from '@shared/services/user.service';
import { firstValueFrom, Observable } from 'rxjs';
import { CreateEstimateQuery } from './models/create-estimate-query.model';

@Component({
  selector: 'pop-create-estimate',
  templateUrl: './create-estimate.component.html',
  styleUrls: ['./create-estimate.component.less']
})
export class CreateEstimateComponent extends BaseComponent implements OnInit {
  public companies?: Company[];
  public config?: EstimateConfiguration;
  public contact?: string;
  public currencies?: CompanyCurrency[];
  public customers?: CustomerDto[];
  public departments?: Department[];
  public description?: string;
  public endDate?: Date;
  public estimateBudgetTypes = new EstimateBudgetTypes();
  public estimateTypeValues: EnumItem<EstimateTypeEnum>[];
  public expenseBillingMethodCodes$: Observable<string[]>;
  public internalFeeBillingMethodCodes$: Observable<string[]>;
  public laborBillingMethodCodes$: Observable<string[]>;
  public minDate?: Date;
  public projectTemplates?: string[];
  public rate?: number;
  public referenceCode?: string;
  public resources?: Resource[];
  public selectedCurrencyCode?: string;
  public selectedCustomerNumber?: string;
  public selectedDepartmentCode?: string;
  public selectedEstimateType: EstimateTypeEnum = EstimateTypeEnum.New;
  public selectedExpenseBillingMethodCode?: string;
  public selectedInternalFeeBillingMethodCode?: string;
  public selectedLaborBillingMethodCode?: string;
  public selectedProject?: Project;
  public selectedProjectManagerId?: string;
  public selectedProjectTemplateCode?: string;
  public startDate?: Date;

  public get isEstimateTypeNew() {
    return this.selectedEstimateType === EstimateTypeEnum.New;
  }

  private _selectedCompanyCode: string | undefined;
  public get selectedCompanyCode(): string | undefined {
    return this._selectedCompanyCode;
  }
  public set selectedCompanyCode(v: string | undefined) {
    if (this._selectedCompanyCode === v) {
      return;
    }
    this._selectedCompanyCode = v;
    this.onCompanySelected();
  }

  public constructor(
    private estimateService: EstimateService,
    private currencyService: CurrencyService,
    private estimateSettingsService: EstimateSettingsService,
    private userService: UserService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loadingService: LoadingService
  ) {
    super();
    this.estimateTypeValues = EstimateTypeEnumItems;
  }

  public async ngOnInit() {
    this.companies = await this.estimateService.getCompaniesAsync();

    const config = await this.estimateSettingsService.getEstimateConfigurationAsync();
    this.config = { ...config };
    if (this.config.forceMaxBudgeted) {
      this.config.useMaxBudgeted = true;
    }
    if (this.config.forceLaborBillingMethod) {
      this.config.useLaborBillingMethod = true;
    }

    const user = await this.userService.getUserAsync();
    const restrictedCompanies = this.config.restrictedCompanies.split(',');
    this.resources = await this.estimateService.getResources(user, restrictedCompanies);
    this.departments = await this.estimateService.getDepartmentsAsync(user, restrictedCompanies, true);
    this.laborBillingMethodCodes$ = this.estimateService.getLaborBillingMethodCodes();
    this.expenseBillingMethodCodes$ = this.estimateService.getExpenseBillingMethodCodes();
    this.internalFeeBillingMethodCodes$ = this.estimateService.getInternalFeeBillingMethodCodes();

    const templates = await this.estimateService.getProjectTemplatesAsync();
    this.projectTemplates = templates.map(t => t.code);
    this.selectedProjectTemplateCode = this.projectTemplates[0];
  }

  public async onSelectProject(projectItem: ProjectPickerItem) {
    this.selectedProject = projectItem?.project;
    this.referenceCode = undefined;
    this.selectedProjectManagerId = this.selectedProject?.manager?.id;
    this.description = this.selectedProject?.description;
    this.rate = this.selectedProject?.rate;
    this.contact = this.selectedProject?.contact;

    // billing methods
    this.selectedLaborBillingMethodCode = this.selectedProject?.laborBillingMethodCode;
    this.selectedExpenseBillingMethodCode = this.selectedProject?.expenseBillingMethodCode;
    this.selectedInternalFeeBillingMethodCode = this.selectedProject?.internalFeeBillingMethodCode;

    // dates
    this.startDate = this.selectedProject?.startDate;
    if (this.startDate?.getFullYear() === 1900) {
      this.startDate = undefined;
    }
    this.minDate = this.startDate;
    this.endDate = this.isEstimateTypeNew ? this.minDate : undefined;

    // budget type
    this.estimateBudgetTypes.selectedBudgetType =
      this.selectedProject?.budgetType === 1 ? BudgetTypeEnum.Detailed : BudgetTypeEnum.Summary;

    // department
    const selectedDepartment = await this.estimateService.getDepartmentByProjectAsync(this.selectedProject?.code);
    this.selectedDepartmentCode = selectedDepartment?.code;

    this.selectedCompanyCode = this.selectedProject?.companyCode; // will trigger onCompanySelected()
  }

  public async onCompanySelected() {
    // reset on company change
    this.selectedCurrencyCode = undefined;
    this.currencies = undefined;
    this.selectedCustomerNumber = undefined;
    this.customers = undefined;

    if (!this.selectedCompanyCode) {
      return;
    }

    // get clients
    this.customers = await this.estimateService.getCustomerByCompanyAsync(this.selectedCompanyCode);
    this.selectedCustomerNumber = this.selectedProject?.clientCode;

    // get company currencies
    const allCurrencies = await this.currencyService.getCompanyCurrenciesAsync();
    this.currencies = allCurrencies.filter(c => c.companyCode === this.selectedCompanyCode);
    this.selectedCurrencyCode =
      this.selectedProject?.currencyCode ?? this.currencies?.find(c => c.isFunctional)?.currencyCode;
  }

  public async closeModal() {
    const queryParams: CreateEstimateQuery = { newEstimateDialog: undefined };

    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge' // remove to replace all query params by provided
    });
  }

  public async onSubmit() {
    await this.loadingService.run(() => this.createNewEstimateInternal());
  }

  private async createNewEstimateInternal() {
    const client = this.customers?.find(c => c.customerId === this.selectedCustomerNumber);
    const currency = this.currencies?.find(c => c.currencyCode === this.selectedCurrencyCode);

    let newEstimate = this.estimateService.createEstimate({
      projectName: this.description,
      projectCode: this.selectedProject.code,
      companyCode: this.selectedCompanyCode,
      codeRef: this.referenceCode,
      projectModelCode: this.selectedProjectTemplateCode,
      budgetType: this.estimateBudgetTypes.selectedBudgetType,
      budgetTypeExpense: this.estimateBudgetTypes.selectedBudgetTypeExpense,
      budgetTypeTime: this.estimateBudgetTypes.selectedBudgetTypeTime,
      budgetTypeInternalFee: this.estimateBudgetTypes.selectedBudgetTypeInternalFee,
      estimateType: this.selectedEstimateType,
      maxBudgeted: this.config.useMaxBudgeted,
      useLaborBillingMethod: this.config.useLaborBillingMethod,
      projectManagerId: this.selectedProjectManagerId,
      clientCode: client?.customerId,
      clientName: client?.customerName,
      clientContactName: this.contact,
      rate: this.rate,
      dateStart: this.startDate,
      dateEnd: this.endDate,
      laborBillingMethodCode: this.selectedLaborBillingMethodCode,
      expenseBillingMethodCode: this.selectedExpenseBillingMethodCode,
      internalFeeBillingMethodCode: this.selectedInternalFeeBillingMethodCode,
      currencyCode: currency.currencyCode,
      currencyRate: currency.rate,
      departmentCode: this.selectedDepartmentCode,
      // CurrencyRateOperationEnum.Division is the default Operation in GP
      currencyRateOperation: currency.operation,
      limitInternalResourcesByBillingMethod: this.config.limitInternalResourcesByBillingMethod
    }) as Estimate;

    if (this.isEstimateTypeNew) {
      const classifications = await firstValueFrom(this.estimateService.getClassifications());
      const classAll = classifications.find(classification => ClassificationHelper.IsTous(classification.code));

      if (!classAll) {
        const newClassification = this.estimateService.createDefaultClassification('fr') as Classification;
        this.estimateService.createClassificationEstimate(newEstimate.id, newClassification);
      } else {
        this.estimateService.createClassificationEstimate(newEstimate.id, classAll);
      }

      await this.estimateService.saveChangesAsync(true);
    } else {
      // the call to estimateService.retrieveExistingStructures() is done asynchronously on the server because it takes quite some time
      // the PopEntityState is set to Modified at the begining of the operation and Unchanged at the end
      // the estimate detail component presents a loading screen until the state is set to Unchanged
      // because otherwise they see a project structure that is incomplete
      newEstimate.popEntityState = PopEntityStateEnum.Modified;
      await this.estimateService.saveChangesAsync(true);

      newEstimate = await this.estimateService.retrieveExistingStructures();
    }

    this.closeModal();
    this.router.navigate(['/app/estimate-detail', newEstimate.id]);
  }
}
